import { DeliveryClient, IContentItem } from '@kontent-ai/delivery-sdk';
import {
  APIKeys,
  fetchKeys,
  getEnv,
  getEnvConfig,
  envDef,
  getLegacyToken,
  getJWTToken,
} from '../Utilities/KeyLoader';
import { getJWTFromCookie } from '../Utilities/OIDCUtils';
import { debugLog } from '../Utilities/OIDCConfig';

/**
 * Get environment configuration based on hostname
 * @returns environment configuration or null
 */
const getEnvironment = (): envDef | null => {
  const env = getEnv();
  return env ? getEnvConfig(env) : null;
};

/**
 * Check if legacy token parameter exists in URL
 * @returns boolean indicating if token parameter exists
 */
const hasTokenParameter = (): boolean => {
  return getLegacyToken() !== null;
};

/**
 * Used for creating the delivery client that relies on the lambda function for supplying keys
 * rather than the env variables
 * @returns object with keys
 */
const deliveryClientFromKeys = async (): Promise<DeliveryClient> => {
  const keys = await fetchKeys();
  if (!keys) {
    throw new Error('API keys could not be loaded.');
  }
  let previewMode = true;
  if (window.parent && window.parent !== window) {
    // are we in a Moodle iframe?
    previewMode = false; // use "Published" content for Moodle
  }
  const { environmentId, previewKey }: APIKeys = keys;

  const client = new DeliveryClient({
    environmentId: environmentId!,
    previewApiKey: previewKey,
    defaultQueryConfig: {
      usePreviewMode: previewMode, // Queries the Delivery API.
    },
  });

  return client;
};

/**
 * Fetch content from AWS API endpoints using JWT authentication
 * @param filter - Content filter parameter
 * @param value - Content value parameter
 * @param jwtToken - JWT token for authentication
 * @returns Promise with content data
 */
const fetchFromAWSAPI = async (
  filter: string,
  value: string,
  jwtToken: string,
): Promise<any> => {
  const environment = getEnvironment();
  if (!environment) {
    throw new Error('Could not determine environment for AWS API call');
  }

  const encodedFilter = encodeURIComponent(filter);
  const encodedValue = encodeURIComponent(value);
  const contentEndpoint = `${environment.contentEndPoint}/${encodedFilter}/${encodedValue}`;

  try {
    const response = await fetch(contentEndpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include', // Include cookies for additional authentication
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Unknown error' }));
      throw new Error(
        `AWS API request failed: ${response.status} - ${
          errorData.error || response.statusText
        }`,
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching from AWS API:', error);
    throw error;
  }
};

/**
 * Combined content fetching function that implements the new logic:
 * 1. If token parameter exists -> use direct Kontent.ai calls (legacy override)
 * 2. If JWT cookie exists -> use AWS API endpoints with JWT authentication
 * 3. Otherwise -> fall back to direct Kontent.ai calls via fetchKeys
 *
 * @param filter - Content filter parameter
 * @param value - Content value parameter
 * @returns Promise with content data
 */
const getKontent = async (filter: string, value: string): Promise<any> => {
  // Helper function for direct Kontent.ai calls
  const fetchFromKontent = async (): Promise<any> => {
    const deliveryClient = await deliveryClientFromKeys();
    const response = deliveryClient
      .items<IContentItem>()
      .equalsFilter(filter, value)
      .depthParameter(8)
      .toPromise();
    return (await response) as any;
  };

  // Automatic source selection
  try {
    // Priority 1: If token parameter exists, use direct Kontent.ai calls (legacy support)
    if (hasTokenParameter()) {
      debugLog(
        '[Content API] Using direct Kontent.ai calls due to legacy token parameter override',
      );
      return fetchFromKontent();
    }

    // Priority 2: Modern JWT-based authentication - use AWS API endpoints
    debugLog(
      '[Content API] Using JWT-based authentication for AWS API endpoints',
    );

    // Try to get existing JWT from cookie, or generate new one via OIDC
    let jwtToken = getJWTFromCookie();
    if (!jwtToken) {
      jwtToken = await getJWTToken();
    }

    if (jwtToken) {
      debugLog('[Content API] Using AWS API endpoints with JWT authentication');
      return fetchFromAWSAPI(filter, value, jwtToken);
    }

    // If no JWT available, throw error (no fallback to Kontent.ai for modern flow)
    throw new Error('No JWT token available for content authentication');
  } catch (error) {
    console.error('[Content API] Error in getKontent:', error);
    throw error;
  }
};

// Export all functions at the end of the file
export { getKontent };
